import { useEffect, useRef, useState, useCallback } from "react";

type PopupStatus =
  | "idle"
  | "opening"
  | "in_progress"
  | "success"
  | "error"
  | "closed"
  | "timeout";

const CHILD_ORIGINS = new Set([
  "http://localhost:3001",
  "https://app.proofup.com",
]);
const POPUP_CHECK_INTERVAL = 500;
const POPUP_TIMEOUT = 5 * 60 * 1000;

export function useChildPopup() {
  const [status, setStatus] = useState<PopupStatus>("idle");
  const [result, setResult] = useState<any>(null);

  const popupRef = useRef<Window | null>(null);
  const childOriginRef = useRef<string | null>(null);
  const nonceRef = useRef<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // full cleanup
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      popupRef.current?.close();
    } catch {}
    popupRef.current = null;
    childOriginRef.current = null;
    nonceRef.current = null;
    startTimeRef.current = null;
  }, []);

  // reset to allow retry
  const reset = useCallback(() => {
    cleanup();
    setResult(null);
    setStatus("idle");
  }, [cleanup]);

  const openPopup = useCallback(
    (childUrl: string) => {
      // allow retry unless popup is actively opening/in-progress
      if (status === "opening" || status === "in_progress") return;

      const url = new URL(childUrl);
      if (!CHILD_ORIGINS.has(url.origin)) {
        setStatus("error");
        throw new Error("Unapproved child origin");
      }

      childOriginRef.current = url.origin;
      setStatus("opening");

      // Use a unique name to force a new window each time.
      const windowName = `childPopup_${crypto.randomUUID()}`;

      popupRef.current = window.open(
        childUrl,
        windowName,
        "width=600,height=800"
      );

      if (!popupRef.current) {
        setStatus("error");
        return;
      }

      // set nonce for this run (optional but recommended)
      nonceRef.current = crypto.randomUUID();

      setStatus("in_progress");
      startTimeRef.current = Date.now();

      // monitor close/timeout
      timerRef.current = window.setInterval(() => {
        const p = popupRef.current;
        if (!p) {
          cleanup();
          setStatus("closed");
          return;
        }

        if (p.closed) {
          cleanup();
          setStatus((prev) => (prev === "success" ? "success" : "closed"));
        }

        if (
          startTimeRef.current &&
          Date.now() - startTimeRef.current > POPUP_TIMEOUT
        ) {
          try {
            p.close();
          } catch {}
          cleanup();
          setStatus("timeout");
        }
      }, POPUP_CHECK_INTERVAL);
    },
    [status, cleanup]
  );

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // quick validations
      if (!event.origin) return;
      if (!CHILD_ORIGINS.has(event.origin)) return;

      // ensure message is from the popup we opened
      if (event.origin !== childOriginRef.current) return;

      const { type, payload, nonce } = (event.data || {}) as any;

      if (type === "READY") {
        // send INIT only to the child origin we expect
        popupRef.current?.postMessage(
          {
            type: "INIT",
            parentOrigin: window.location.origin,
            nonce: nonceRef.current,
          },
          childOriginRef.current!
        );
        return;
      }

      // if we use nonce, validate it
      if (nonceRef.current && nonce !== nonceRef.current) {
        // ignore mismatched run
        return;
      }

      if (type === "FLOW_SUCCESS") {
        cleanup();
        setResult(payload);
        setStatus("success");
      } else if (type === "FLOW_ERROR") {
        cleanup();
        setResult(payload);
        setStatus("error");
      }
    };

    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
      cleanup();
    };
  }, [cleanup]);

  // helper to retry: resets then opens
  const retry = useCallback(
    (childUrl: string) => {
      reset();
      // small microtask delay to ensure state reset propagated
      setTimeout(() => openPopup(childUrl), 0);
    },
    [openPopup, reset]
  );

  return {
    openPopup,
    retry,
    reset,
    status,
    result,
    isLoading: status === "opening" || status === "in_progress",
  };
}
